import React, { useState } from 'react';
import { toast } from 'react-toastify';
import Input from '../../ui/input';
import Button from '../../ui/button';
import { alertDelete, alertBulkDelete } from '../../../redux/api/alertAPI';
import { useTranslation } from 'react-i18next';

const DeleteAlertModal = ({ isOpen, onClose, data, requireTypedConfirmation = false }) => {
    const {t} = useTranslation("common")
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [deleteMSG, setDeleteMSG] = useState("");
    const [MSGError, setMSGError] = useState("");

    const closeModal = (refresh = false) => {
        if (loading) return;
        setError(null);
        setDeleteMSG("");
        setMSGError("");
        onClose(refresh);
    };

    const handleChange = (e) => {
        e.preventDefault();
        setMSGError("");
        setError(null);
        setDeleteMSG(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const alertsToDelete = Array.isArray(data) ? data : [data.Alert_ID];

            if (requireTypedConfirmation && deleteMSG.trim() !== String(alertsToDelete.length)) {
                setMSGError("Confirmation message does not match.");
                setLoading(false);
                return;
            }

            if (alertsToDelete.length === 0) {
                setLoading(false);
                closeModal();
                return;
            }

            const isBulkDelete = alertsToDelete.length > 1;

            if (isBulkDelete) {
                // One request for the whole batch instead of one HTTP call per
                // alert — firing hundreds/thousands of parallel DELETE calls
                // used to race each other on the backend's shared DB session
                // and silently drop a chunk of them under load.
                const { error, data: result } = await alertBulkDelete(alertsToDelete, { skipSuccessToast: true });

                if (error) {
                    setError(error);
                } else {
                    const successCount = result?.deleted ?? alertsToDelete.length;
                    const failedCount = result?.failed ?? 0;
                    if (successCount > 0) {
                        toast.success(`${successCount} alert(s) successfully deleted.`);
                    }
                    if (failedCount > 0) {
                        toast.error(`${failedCount} alert(s) could not be deleted.`);
                    }
                    closeModal(true);
                }
            } else {
                const { error } = await alertDelete(alertsToDelete[0]);
                if (error) {
                    setError(error);
                } else {
                    closeModal(true);
                }
            }
        } catch (err) {
            console.error("Error Deleting Alert(s):", err);
            setError("An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    };
    
    if (!isOpen) {
        return null;
    }

    const isBulk = Array.isArray(data);
    const count = isBulk ? data.length : 1;

    return (
    <div data-testid="alert-delete-modal" className="grid space-y-4 w-auto m-2 bg-white p-8 rounded-3xl shadow-lg">
        <p className="text-center text-xl text-[#4C4C66] font-bold">{t('delete_confirmation')}</p>
        <p className="text-center text-md text-[#4C4C66] font-normal">
            {requireTypedConfirmation
                ? `Type "${count}" to confirm.`
                : isBulk
                ? `Are you sure you want to delete ${count} alerts?`
                : t('are_you_sure_that_you_want_to_delete_this_alert')
            }
        </p>
        {requireTypedConfirmation && (
            <Input
                data-testid="alert-delete-confirmation"
                type="text"
                placeholder={String(count)}
                value={deleteMSG}
                onChange={handleChange}
                className="w-full"
            />
        )}
        {MSGError && <p className="text-center text-red-500">{MSGError}</p>}
        {error && (
            <p className="text-center text-red-500 bg-red-100 p-2 rounded-md">{error}</p>
        )}
        <div className='flex justify-center pt-4 space-x-5'>
            <Button onClick={() => closeModal()} size="large" color="cancel" disabled={loading}>
                {t('cancel')}
            </Button>
            <Button data-testid="alert-delete-submit" onClick={handleSubmit} type="submit" size="large" disabled={loading}>
                {loading ? t('deleting') : t('delete')}
            </Button>
        </div>
    </div>
    );
};

export default DeleteAlertModal;
